import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { TeamApi } from '@core/requests/api/team/team.api'
import { PlayerApi } from '@core/requests/api/player/player.api'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { catchRequestError, completeWhen } from '@mediacoach/ui'
import { Competition, MatchDayResponse, Season } from '@core/models/season-competition.models'
import { throwError } from 'rxjs'
import {
  fetchSelectionCommonMatchDays,
  fetchSelectionPlayers,
  fetchSelectionTeams,
  setSelectionCommonLastDayWithMatches,
  setSelectionCommonMatchDays,
  setSelectionPlayers,
  setSelectionPlayersLoader,
  setSelectionTeams,
  setSelectionTeamsLoader
} from '@features/selections/store/actions/selections.actions'
import { isGoalKeeper } from '@core/utils/demarcation.utils'
import { partition } from 'lodash-es'
import { Player } from '@core/models/player.models'
import { SeasonApi } from '@core/requests/api/season/season.api'
import { selectFavSeasonCompetitionWithFallback } from '@features/seasons/state/selectors/seasons.selectors'

@Injectable()
export class SelectionsEffects {
  private readonly _actions$ = inject(Actions)
  private readonly _store = inject(Store)
  private readonly _seasonApi: SeasonApi = inject(SeasonApi)
  private readonly _teamApi: TeamApi = inject(TeamApi)
  private readonly _playerApi: PlayerApi = inject(PlayerApi)

  fetchSelectionTeams$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchSelectionTeams),
      tap(() => this._store.dispatch(setSelectionTeamsLoader({ teamsLoading: true }))),
      switchMap(({ competition, season, callingContext }) =>
        this._store.select(selectFavSeasonCompetitionWithFallback(callingContext)).pipe(
          completeWhen((data: { season: Season; competition: Competition }) => !!data?.competition && !!data?.season),
          map((data: { season: Season; competition: Competition }) => ({
            competitionId: competition?.id ?? data.competition.id,
            seasonId: season?.id ?? data.season.id
          }))
        )
      ),
      switchMap(({ competitionId, seasonId }: { competitionId: string; seasonId: string }) =>
        this._teamApi.fetchTeams(seasonId, competitionId).pipe(
          catchError((err) => {
            console.error('Unable to fetch current competition teams', err)
            this._store.dispatch(setSelectionTeamsLoader({ teamsLoading: false }))
            return throwError(() => err)
          })
        )
      ),
      switchMap(({ teams }) => [setSelectionTeams({ teams }), setSelectionTeamsLoader({ teamsLoading: false })])
    )
  )

  fetchSelectionPlayers$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchSelectionPlayers),
      tap(() => this._store.dispatch(setSelectionPlayersLoader({ playersLoading: true }))),
      switchMap(({ competition, season, query, player }) =>
        this._playerApi.searchPlayer(query, competition?.id, season?.id).pipe(
          map((players) => {
            const isGoalkeeper = isGoalKeeper(player)
            const [goalkeepers, fieldPlayers] = partition(players || [], (p) => isGoalKeeper(p))
            return isGoalkeeper ? goalkeepers : fieldPlayers
          }),
          catchError((err) => {
            console.error(
              `Unable to search player "${query}" from season ${season?.officialName} and competition: ${competition?.officialName}`,
              err
            )
            this._store.dispatch(setSelectionPlayersLoader({ playersLoading: false }))
            return throwError(() => err)
          })
        )
      ),
      switchMap((players: Player[]) => [
        setSelectionPlayersLoader({ playersLoading: false }),
        setSelectionPlayers({ players })
      ])
    )
  )

  fetchSelectionCommonMatchDays$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchSelectionCommonMatchDays),
      switchMap(({ season, competition }) =>
        this._seasonApi.fetchMatchDaysBySeasonAndCompetition(season, competition).pipe(catchRequestError())
      ),
      switchMap(({ items, lastRegularMatchDayWithLiveOrFulltimeMatches }: MatchDayResponse) => [
        setSelectionCommonMatchDays({ commonMatchDays: items }),
        setSelectionCommonLastDayWithMatches({
          commonLastMatchDayIdWithMatches: lastRegularMatchDayWithLiveOrFulltimeMatches
        })
      ])
    )
  )
}
